import { memo, useEffect, useState, type ReactNode } from 'react'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'

import { LoadingState } from '~/shared/ui/LoadingState'

import { SCREEN_LOAD_TIMEOUT_MS } from './config'

import styles from './ScreenLoadGate.module.scss'

/**
 * Экран показывается целиком и сразу, а не собирается на глазах.
 *
 * Секции экрана — независимые виджеты со своими запросами (правило 4), и каждая подставляла
 * контент своей высоты в свой момент: прогноз, вчерашний ответ, настроение, тизеры, ряд «из
 * твоих уз». Экран дёргался несколько раз подряд, причём тем сильнее, чем медленнее сеть.
 *
 * Дети монтируются сразу — иначе их запросы просто не начнутся, — но до готовности скрыты
 * `visibility`, а не размонтированы: раскладка успевает посчитаться, и в момент показа ничего
 * не пересчитывается. Поверх стоит один общий индикатор.
 *
 * Считается по счётчику активных запросов react-query. Нулевой счётчик сам по себе не значит
 * «грузить нечего»: `useIsFetching` отдаёт значение НА МОМЕНТ РЕНДЕРА, а запросы детей стартуют
 * в их эффектах, то есть уже после того, как это значение посчитано. На первом коммите счётчик
 * всегда ноль — гейт открывался мгновенно, и экран собирался на глазах ровно так, как будто
 * его нет (главный экран: скелетон прогноза 88px → текст 148px, полоса «вчера» 64px → ноль,
 * настроение 120px → 188px, и всё это уже на видимом экране).
 *
 * Поэтому на ноль реагируем не сразу, а на следующем тике и по живому счётчику клиента:
 * к этому моменту эффекты детей отработали, и ноль означает настоящий ноль.
 *
 * Гейт «одноразовый»: после первого показа фоновые перезапросы (refetch при возврате на вкладку,
 * инвалидация после мутации) экран уже не прячут.
 */
export const ScreenLoadGate = memo(function ScreenLoadGate(props: { children: ReactNode }) {
  const fetchingCount = useIsFetching()
  const queryClient = useQueryClient()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (fetchingCount > 0) return undefined

    const timer = setTimeout(() => {
      if (queryClient.isFetching() === 0) setIsReady(true)
    }, 0)

    return () => clearTimeout(timer)
  }, [fetchingCount, queryClient])

  // Один зависший запрос не имеет права держать весь экран пустым.
  useEffect(() => {
    if (isReady) return

    const timer = setTimeout(() => setIsReady(true), SCREEN_LOAD_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isReady])

  return (
    <div className={styles.root}>
      {!isReady && (
        <div className={styles.loader}>
          <LoadingState size='L' />
        </div>
      )}
      <div className={cn(styles.content, !isReady && styles.isHidden)} aria-busy={!isReady}>
        {props.children}
      </div>
    </div>
  )
})
